import { useState, type KeyboardEvent } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Film,
  ImageOff,
  Maximize2,
  Pencil,
  Sparkles,
  Trash2,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toast';
import { ConfirmDestructiveDialog } from '@/components/confirm-destructive-dialog';
import { RemoteImage } from '@/components/remote-image';
import { useDeleteMedia, useRegenerateMediaText } from '@/hooks/use-media';
import { describeError } from '@/lib/api-client';
import {
  channelLabel,
  firstSharerName,
  formatAbsoluteTitle,
  formatBytes,
  formatDateTime,
  formatDuration,
} from '@/lib/format';
import type { MediaItem } from '@/types/api';
import { MediaEditDialog } from './media-edit-dialog';
import { describeMedia } from './media-tile';

/**
 * The full view of one image or video, with where it came from. Left/right arrow keys (and the two
 * buttons) step through the files already loaded on the page — except while the video player has
 * focus, where those keys seek and belong to it.
 *
 * SECURITY, DELIBERATE: the file name and title are the uploader's own text relayed by Slack, and a
 * generated title and description are a model's reading of the file — including any text in it.
 * All of them are rendered as text nodes only, like every other string in this app.
 */
export function MediaViewerDialog({
  items,
  index,
  onIndexChange,
  onClose,
  canManage,
}: {
  items: MediaItem[];
  /** The open file's position in `items`, or null when the viewer is closed. */
  index: number | null;
  onIndexChange: (index: number) => void;
  onClose: () => void;
  /** Administrators edit, regenerate and delete. */
  canManage: boolean;
}) {
  const media = index !== null ? items[index] : undefined;
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [editing, setEditing] = useState(false);
  const deleteMedia = useDeleteMedia();
  const regenerate = useRegenerateMediaText();
  const { toast } = useToast();

  const hasPrevious = index !== null && index > 0;
  const hasNext = index !== null && index < items.length - 1;
  const noun = media?.kind === 'video' ? 'video' : 'image';

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (index === null) return;
    if (event.target instanceof HTMLMediaElement || event.target instanceof HTMLInputElement)
      return;
    if (event.key === 'ArrowLeft' && hasPrevious) onIndexChange(index - 1);
    else if (event.key === 'ArrowRight' && hasNext) onIndexChange(index + 1);
    else return;
    event.preventDefault();
  };

  const remove = () => {
    if (!media) return;
    deleteMedia.mutate(media.mediaId, {
      onSuccess: () => {
        setConfirmingDelete(false);
        onClose();
        toast({
          title: media.kind === 'video' ? 'Video deleted' : 'Image deleted',
          description: 'Sharing it again in a monitored channel will collect it afresh.',
          variant: 'success',
        });
      },
      onError: (error) =>
        toast({
          title: `Could not delete the ${noun}`,
          description: describeError(error),
          variant: 'error',
        }),
    });
  };

  const askForNewText = () => {
    if (!media) return;
    regenerate.mutate(media.mediaId, {
      onSuccess: () =>
        toast({
          title: 'Generating a new title',
          description:
            media.kind === 'video'
              ? 'It appears here in a few minutes, once the video has been watched and listened to.'
              : 'It appears here in a few seconds.',
          variant: 'success',
        }),
      onError: (error) =>
        toast({
          title: 'Could not ask for a new title',
          description: describeError(error),
          variant: 'error',
        }),
    });
  };

  const share = media?.firstShare;
  const dimensions = media?.width && media.height ? `${media.width} × ${media.height}` : undefined;

  return (
    <>
      <Dialog open={media !== undefined} onOpenChange={(open) => (open ? undefined : onClose())}>
        <DialogContent className="max-w-4xl gap-3 p-4 sm:p-6" onKeyDown={onKeyDown}>
          {media && share ? (
            <>
              <DialogHeader>
                <DialogTitle className="truncate">
                  {media.title ?? media.name ?? (media.kind === 'video' ? 'Video' : 'Image')}
                </DialogTitle>
                <DialogDescription>
                  Shared by{' '}
                  <span className="font-medium text-foreground">{firstSharerName(share)}</span> in{' '}
                  <span className="font-medium text-foreground">{channelLabel(share)}</span>,{' '}
                  <time dateTime={share.sharedAt} title={formatAbsoluteTitle(share.sharedAt)}>
                    {formatDateTime(share.sharedAt)}
                  </time>
                </DialogDescription>
              </DialogHeader>

              {media.description || media.textSource === 'generated' ? (
                <div className="flex flex-col gap-1.5 text-sm">
                  {media.description ? <p>{media.description}</p> : null}
                  {media.textSource === 'generated' ? (
                    <Badge
                      variant="muted"
                      className="self-start"
                      title="Written by a model from what the file shows and says"
                    >
                      <Sparkles aria-hidden="true" className="h-3 w-3" />
                      Generated
                    </Badge>
                  ) : null}
                </div>
              ) : null}

              <div className="relative flex min-h-48 items-center justify-center overflow-hidden rounded-md bg-muted">
                {media.kind === 'video' ? (
                  <VideoPlayer key={media.mediaId} media={media} />
                ) : (
                  <RemoteImage
                    key={media.mediaId}
                    src={media.url}
                    alt={describeMedia(media)}
                    loading="eager"
                    className="max-h-[65vh] w-auto max-w-full object-contain"
                    wrapperClassName="h-48 w-full"
                    fallback={
                      <span className="flex flex-col items-center gap-2 text-sm text-muted-foreground">
                        <ImageOff className="h-6 w-6" />
                        The image could not be loaded. Close and reopen it to get a fresh link.
                      </span>
                    }
                  />
                )}
                {hasPrevious ? (
                  <Button
                    type="button"
                    variant="secondary"
                    size="icon"
                    className="absolute left-2 top-1/2 -translate-y-1/2 opacity-90"
                    aria-label="Previous"
                    onClick={() => onIndexChange((index ?? 0) - 1)}
                  >
                    <ChevronLeft aria-hidden="true" className="h-4 w-4" />
                  </Button>
                ) : null}
                {hasNext ? (
                  <Button
                    type="button"
                    variant="secondary"
                    size="icon"
                    className="absolute right-2 top-1/2 -translate-y-1/2 opacity-90"
                    aria-label="Next"
                    onClick={() => onIndexChange((index ?? 0) + 1)}
                  >
                    <ChevronRight aria-hidden="true" className="h-4 w-4" />
                  </Button>
                ) : null}
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <dl className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  {media.name ? (
                    <div className="flex min-w-0 gap-1">
                      <dt className="sr-only">File</dt>
                      <dd className="max-w-56 truncate" title={media.name}>
                        {media.name}
                      </dd>
                    </div>
                  ) : null}
                  {media.durationMs !== undefined ? (
                    <div className="flex gap-1">
                      <dt className="sr-only">Length</dt>
                      <dd className="tabular-nums">{formatDuration(media.durationMs)}</dd>
                    </div>
                  ) : null}
                  {dimensions ? (
                    <div className="flex gap-1">
                      <dt className="sr-only">Dimensions</dt>
                      <dd className="tabular-nums">{dimensions}</dd>
                    </div>
                  ) : null}
                  <div className="flex gap-1">
                    <dt className="sr-only">Size</dt>
                    <dd className="tabular-nums">{formatBytes(media.sizeBytes)}</dd>
                  </div>
                </dl>

                <div className="flex flex-wrap gap-2">
                  {share.permalink ? (
                    <Button asChild variant="outline" size="sm">
                      <a href={share.permalink} target="_blank" rel="noreferrer noopener">
                        <ExternalLink aria-hidden="true" className="h-4 w-4" />
                        Open in Slack
                      </a>
                    </Button>
                  ) : null}
                  {media.url ? (
                    <Button asChild variant="outline" size="sm">
                      <a href={media.url} target="_blank" rel="noreferrer noopener">
                        <Maximize2 aria-hidden="true" className="h-4 w-4" />
                        {media.kind === 'video' ? 'Open original' : 'Full size'}
                      </a>
                    </Button>
                  ) : null}
                  {canManage ? (
                    <>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setEditing(true)}
                      >
                        <Pencil aria-hidden="true" className="h-4 w-4" />
                        Edit
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        loading={regenerate.isPending}
                        onClick={askForNewText}
                      >
                        <Sparkles aria-hidden="true" className="h-4 w-4" />
                        Regenerate title
                      </Button>
                    </>
                  ) : null}
                  {canManage ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setConfirmingDelete(true)}
                    >
                      <Trash2 aria-hidden="true" className="h-4 w-4" />
                      Delete
                    </Button>
                  ) : null}
                </div>
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      <ConfirmDestructiveDialog
        open={confirmingDelete}
        onOpenChange={setConfirmingDelete}
        title={`Delete this ${noun}?`}
        description={`The stored copy is removed from SLC for everyone. The message in Slack is not touched, and sharing the same ${noun} again in a monitored channel collects it afresh.`}
        confirmPhrase="DELETE"
        pending={deleteMedia.isPending}
        onConfirm={remove}
      />

      {media && canManage ? (
        <MediaEditDialog media={media} open={editing} onOpenChange={setEditing} />
      ) : null}
    </>
  );
}

/**
 * The browser's own player, straight from the presigned URL — it asks S3 for byte ranges as it
 * goes, so nothing is downloaded before someone presses play (`preload="metadata"`). Not every
 * stored container plays in every browser (an HEVC `.mov` in Chrome, say); when it cannot, the
 * original can still be opened or saved.
 */
function VideoPlayer({ media }: { media: MediaItem }) {
  const [failed, setFailed] = useState(false);

  if (!media.url || failed) {
    return (
      <span className="flex flex-col items-center gap-2 px-6 py-12 text-center text-sm text-muted-foreground">
        <Film aria-hidden="true" className="h-6 w-6" />
        {media.url
          ? 'Your browser cannot play this video here. Use “Open original” to download it.'
          : 'The video could not be loaded. Close and reopen it to get a fresh link.'}
      </span>
    );
  }

  return (
    <video
      src={media.url}
      poster={media.thumbnailUrl}
      controls
      preload="metadata"
      playsInline
      aria-label={describeMedia(media)}
      onError={() => setFailed(true)}
      className="max-h-[65vh] w-full bg-black"
    >
      Your browser cannot play this video.
    </video>
  );
}
